import { state } from "./index";
import Sprite from "./sprite";

import {
  setPositionsAttrib,
  setTextureCoordsAttrib,
  setNormalsAttrib,
  setTangentsAttrib,
  setBitangentsAttrib,
  setBitangentsAttribDirect,
  setTangentsAttribDirect,
  setNormalsAttribDirect,
  setOpacitiesAttrib
} from "./helpers/webgl-helpers";

import { QUAD_POSITIONS, FUDGED_QUAD_TEXTURE_COORDS } from "./models";

export default class SpriteRenderer {
  static VERTEX_POSITIONS_LENGTH = QUAD_POSITIONS.length;
  static TEXTURE_COORDS_LENGTH = FUDGED_QUAD_TEXTURE_COORDS.length;
  static OPACITIES_LENGTH = QUAD_POSITIONS.length;
  static MODEL_MATRIX_LENGTH = 16;
  static NORMAL_LENGTH = 36;
  static TANGENT_LENGTH = 36;
  static BITANGENT_LENGTH = 36;

  public sprites: Sprite[];

  public vertexPositions: number[] = [];
  public modelMatrices: number[] = [];
  public textureCoords: number[] = [];
  public vertexNormals: Float32Array = new Float32Array();
  public vertexTangents: Float32Array = new Float32Array();
  public vertexBitangents: Float32Array = new Float32Array();
  public opacities: number[] = [];

  private gl: WebGLRenderingContext;
  private programName: string;
  private buffers: {
    positions: WebGLBuffer | null;
    normals: WebGLBuffer | null;
    tangents: WebGLBuffer | null;
    bitangents: WebGLBuffer | null;
    textureCoords: WebGLBuffer | null;
    modelMatrices: WebGLBuffer | null;
    opacities: WebGLBuffer | null;
  };

  constructor(gl: WebGLRenderingContext, programName: string) {
    this.gl = gl;
    this.programName = programName;
    this.sprites = [];
    this.buffers = {
      positions: gl.createBuffer(),
      textureCoords: gl.createBuffer(),
      normals: gl.createBuffer(),
      tangents: gl.createBuffer(),
      bitangents: gl.createBuffer(),
      modelMatrices: gl.createBuffer(),
      opacities: gl.createBuffer()
    };
  }

  public removeAll() {
    clearArray(this.sprites);
    clearSpriteRenderer(this.sprites);
  }

  // Sort by the array texture so we can hopefully batch all the
  // draw calls with the same texture in a row
  public add(sprite: Sprite) {
    this.sprites.push(sprite);
    sprite.spriteRenderer = this;
  }

  public sort() {
    // The textureURL comparison is essentially random
    // the currentPlayer check is to keep the currentPlayer on top of everything else
    this.sprites.sort((a, b) => {
      const aCurrent = a === state.currentPlayer ? "Z" : "A";
      const bCurrent = b === state.currentPlayer ? "Z" : "A";
      const aKey = aCurrent + a.spriteRenderRenderOrderHint + a.textureUrl;
      const bKey = bCurrent + b.spriteRenderRenderOrderHint + b.textureUrl;

      if (aKey == bKey) return 0;

      if (aKey > bKey) {
        return 1;
      } else {
        return -1;
      }
    });

    this.vertexBitangents = new Float32Array(this.sprites.length * SpriteRenderer.BITANGENT_LENGTH);
    this.vertexTangents = new Float32Array(this.sprites.length * SpriteRenderer.TANGENT_LENGTH);
    this.vertexNormals = new Float32Array(this.sprites.length * SpriteRenderer.NORMAL_LENGTH);

    this.sprites.forEach(sprite => {
      const index = this.sprites.indexOf(sprite);
      sprite.spriteRendererIndex = index;
      sprite.setTbnOnSpriteRenderer();
      sprite.setTextureCoordinatesOnSpriteRenderer();
      sprite.setVertexPositionsOnSpriteRenderer();
      sprite.setOpacitiesOnSpriteRenderer();
    });
  }

  public render(timestamp: number) {
    if (this.sprites.length === 0) return;

    const { gl, buffers, programName } = this;
    const { programWrappers } = state;
    const programWrapper = programWrappers[programName];
    const { cache } = programWrapper;

    if (!buffers["positions"]) throw Error(`No positions buffer...`);
    if (!buffers["textureCoords"]) throw Error(`No texture coordinates buffer...`);
    const vertexPositions = [];
    const modelMatrices = [];
    const textureCoords = [];
    const vertexNormals = [];
    const vertexTangents = [];
    const vertexBitangents = [];
    const opacities = [];

    let activeTextureUrl = null;

    // Every time we get a new texture, make a draw call and start over
    for (let i = 0; i < this.sprites.length; i++) {
      const sprite = this.sprites[i];

      // If we're switching textures, draw
      if (activeTextureUrl !== sprite.textureUrl) {
        if (activeTextureUrl) {
          this.draw(
            vertexPositions,
            textureCoords,
            vertexNormals,
            vertexTangents,
            vertexBitangents,
            opacities
          );
        }

        const texture = sprite.texture;
        const normalTexture = sprite.normalTexture;

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, normalTexture);
        gl.uniform1i(cache.uniforms.uSpriteSampler, 0);
        gl.uniform1i(cache.uniforms.uNormalSampler, 1);

        activeTextureUrl = sprite.textureUrl;
      }

      textureCoords.push(...sprite.textureCoords);
      vertexPositions.push(...sprite.vertexPositions);
      vertexNormals.push(new Float32Array(sprite.vertexNormals));
      vertexTangents.push(new Float32Array(sprite.vertexTangents));
      vertexBitangents.push(new Float32Array(sprite.vertexBitangents));
      modelMatrices.push(...sprite.modelMatrix);
      opacities.push(...QUAD_POSITIONS.map(() => sprite.opacity));
    }

    // NOTE: I commented this out after I added the current player thing... I don't know why, and I'm scared.
    // Draw one last time at the end
    this.draw(
      vertexPositions,
      textureCoords,
      vertexNormals,
      vertexTangents,
      vertexBitangents,
      opacities
    );
  }

  public renderNoGarbage(timestamp: number) {
    if (this.sprites.length === 0) return;

    const { gl, buffers, programName } = this;
    const {
      vertexPositions,
      modelMatrices,
      textureCoords,
      vertexNormals,
      vertexTangents,
      vertexBitangents,
      opacities
    } = this;

    const { programWrappers } = state;
    const programWrapper = programWrappers[programName];
    const { cache } = programWrapper;

    if (!buffers["positions"]) throw Error(`No positions buffer...`);
    if (!buffers["textureCoords"]) throw Error(`No texture coordinates buffer...`);

    let activeTextureUrl: string = this.sprites[0].textureUrl;
    let activeTexture: WebGLTexture = this.sprites[0].texture;
    let activeNormalTexture: WebGLTexture = this.sprites[0].normalTexture;
    let startIndex = 0;
    let endIndex = 0;

    function bindActiveTexture() {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, activeTexture);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, activeNormalTexture);
      gl.uniform1i(cache.uniforms.uSpriteSampler, 0);
      gl.uniform1i(cache.uniforms.uNormalSampler, 1);
    }

    for (let i = 0; i < this.sprites.length; i++) {
      const sprite = this.sprites[i];

      if (activeTextureUrl !== sprite.textureUrl) {
        bindActiveTexture();
        endIndex = i - 1;
        this.drawNoGarbage(startIndex, endIndex);
        startIndex = i;
      }

      if (sprite.texture) {
        activeTextureUrl = sprite.textureUrl;
        activeTexture = sprite.texture;
        activeNormalTexture = sprite.normalTexture;
      } else {
        console.warn(`No texture for ${sprite.name}`);
      }
    }

    bindActiveTexture();
    this.drawNoGarbage(startIndex, this.sprites.length - 1);
  }

  drawNoGarbage(startIndex: number, endIndex: number) {
    const { gl, buffers, programName } = this;
    const {
      vertexPositions,
      textureCoords,
      vertexNormals,
      vertexTangents,
      vertexBitangents,
      opacities
    } = this;

    const lastSliceIndex = endIndex + 1;

    const vertexPositionSlices = vertexPositions.slice(
      startIndex * SpriteRenderer.VERTEX_POSITIONS_LENGTH,
      lastSliceIndex * SpriteRenderer.VERTEX_POSITIONS_LENGTH
    );

    const textureCoordsSlices = textureCoords.slice(
      startIndex * SpriteRenderer.TEXTURE_COORDS_LENGTH,
      lastSliceIndex * SpriteRenderer.TEXTURE_COORDS_LENGTH
    );

    const opacitySlices = opacities.slice(
      startIndex * SpriteRenderer.OPACITIES_LENGTH,
      lastSliceIndex * SpriteRenderer.OPACITIES_LENGTH
    );

    const normalSlices = vertexNormals.subarray(
      startIndex * SpriteRenderer.NORMAL_LENGTH,
      lastSliceIndex * SpriteRenderer.NORMAL_LENGTH
    );

    const tangentSlices = vertexTangents.subarray(
      startIndex * SpriteRenderer.TANGENT_LENGTH,
      lastSliceIndex * SpriteRenderer.TANGENT_LENGTH
    );

    const bitangentSlices = vertexBitangents.subarray(
      startIndex * SpriteRenderer.BITANGENT_LENGTH,
      lastSliceIndex * SpriteRenderer.BITANGENT_LENGTH
    );

    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    setPositionsAttrib(gl, programName, state, buffers.positions, vertexPositionSlices);
    setTextureCoordsAttrib(gl, programName, state, buffers.textureCoords, textureCoordsSlices);
    setOpacitiesAttrib(gl, programName, state, buffers.opacities, opacitySlices);
    setNormalsAttribDirect(gl, programName, state, buffers.normals, normalSlices);
    setTangentsAttribDirect(gl, programName, state, buffers.tangents, tangentSlices);
    setBitangentsAttribDirect(gl, programName, state, buffers.bitangents, bitangentSlices);
    gl.drawArrays(gl.TRIANGLES, 0, vertexPositionSlices.length / 3);
  }

  draw(vertexPositions, textureCoords, vertexNormals, vertexTangents, vertexBitangents, opacities) {
    const { gl, buffers, programName } = this;

    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    setPositionsAttrib(gl, programName, state, buffers.positions, vertexPositions);
    setTextureCoordsAttrib(gl, programName, state, buffers.textureCoords, textureCoords);
    setNormalsAttrib(gl, programName, state, buffers.normals, vertexNormals);
    setTangentsAttrib(gl, programName, state, buffers.tangents, vertexTangents);
    setBitangentsAttrib(gl, programName, state, buffers.bitangents, vertexBitangents);
    setOpacitiesAttrib(gl, programName, state, buffers.opacities, opacities);
    gl.drawArrays(gl.TRIANGLES, 0, vertexPositions.length / 3);
    // Remove everything from the arrays to get ready for the next draw call
    clearArray(vertexPositions);
    clearArray(textureCoords);
    clearArray(vertexNormals);
    clearArray(vertexTangents);
    clearArray(vertexBitangents);
    clearArray(opacities);
  }
}

function clearArray(array) {
  array.splice(0, array.length);
}

function clearSpriteRenderer(array: Sprite[]) {
  array.forEach(sprite => {
    sprite.spriteRenderer = null;
    sprite.spriteRendererIndex = null;
  });
}
